import {
  Alert,
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'

import type { CreateUploadResponse, FormConfigResponse } from '@/services/definitions'
import { CreateUploadTarget, UploadAppFile } from '@/services/IntakeService'
import {
  contentTypeForFilename,
  formatBytes,
  humanizeOption,
  MAX_SLUG_LENGTH,
  MAX_TAG_LENGTH,
  MAX_TAGS,
  MIN_SLUG_LENGTH,
  parseTags,
  SLUG_PATTERN,
  slugify,
  validateAppFile
} from '@/utils/submission'

export interface SubmissionFormValues {
  appName: string
  slug: string
  description: string
  category: string
  intendedAudience: string
  tags: string
}

export interface SubmissionFormProps {
  config: FormConfigResponse
  getIdToken: () => Promise<string>
  onSubmitted: (target: CreateUploadResponse) => void
}

export function SubmissionForm({
  config,
  getIdToken,
  onSubmitted
}: SubmissionFormProps): React.JSX.Element {
  const [file, setFile] = useState<File | null>(null)
  const [fileError, setFileError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    control,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue
  } = useForm<SubmissionFormValues>({
    defaultValues: {
      appName: '',
      slug: '',
      description: '',
      category: config.categories[0] ?? '',
      intendedAudience: config.audiences[0] ?? '',
      tags: ''
    }
  })

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const chosen = event.target.files?.[0] ?? null
    setFile(chosen)
    setFileError(validateAppFile(chosen, config.max_upload_bytes))
  }

  const onValid = async (values: SubmissionFormValues): Promise<void> => {
    const problem = validateAppFile(file, config.max_upload_bytes)
    setFileError(problem)
    if (problem || !file) return

    setSubmitError(null)
    try {
      const idToken = await getIdToken()
      const target = await CreateUploadTarget(
        {
          filename: file.name,
          content_type: contentTypeForFilename(file.name),
          metadata: {
            name: values.appName.trim(),
            description: values.description.trim(),
            slug: values.slug.trim(),
            category: values.category,
            intended_audience: values.intendedAudience,
            tags: parseTags(values.tags)
          }
        },
        idToken
      )
      await UploadAppFile(target, file)

      reset()
      setFile(null)
      setSlugManuallyEdited(false)
      onSubmitted(target)
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Submission failed.')
    }
  }

  return (
    <Box component='form' onSubmit={handleSubmit(onValid)} noValidate>
      <Stack spacing={3}>
        {submitError && <Alert severity='error'>{submitError}</Alert>}

        <Box>
          <Typography component='label' htmlFor='app-file' variant='subtitle2' display='block' gutterBottom>
            App file
          </Typography>
          <input
            id='app-file'
            type='file'
            accept='.html,.md,text/html,text/markdown'
            onChange={onFileChange}
            aria-describedby='app-file-hint'
            aria-invalid={Boolean(fileError)}
          />
          <Typography
            id='app-file-hint'
            variant='body2'
            display='block'
            color={fileError ? 'error' : 'text.secondary'}
            mt={0.5}
          >
            {fileError ?? (
              <>
                One <FileExtension>.html</FileExtension> app or <FileExtension>.md</FileExtension> skill
                file, up to {formatBytes(config.max_upload_bytes)}.
              </>
            )}
          </Typography>
        </Box>

        <TextField
          label='App name'
          placeholder='Expiry Calculator'
          slotProps={{ htmlInput: { maxLength: 80, autoComplete: 'off', 'data-1p-ignore': true } }}
          error={Boolean(errors.appName)}
          helperText={
            errors.appName?.message ??
            "The app's title as people will see it, not your name. You're already signed in as the submitter."
          }
          {...register('appName', {
            required: 'Give the app a name.',
            onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
              if (!slugManuallyEdited) {
                setValue('slug', slugify(event.target.value))
              }
            }
          })}
        />

        {/*
          Controlled via Controller, not register: the name field derives this
          value with setValue, and an uncontrolled MUI TextField never learns
          about a programmatic write — its label stays unfloated and overlaps the
          derived text. Passing value through makes the label track it.
        */}
        <Controller
          name='slug'
          control={control}
          // Mirrors every constraint the broker enforces on `slug`, so a
          // violation is an inline message rather than a round-trip to a 422.
          // MIN_SLUG_LENGTH matters in practice: a one-character name derives a
          // one-character slug, which satisfies the pattern.
          rules={{
            required: 'A URL slug is required.',
            minLength: {
              value: MIN_SLUG_LENGTH,
              message: `At least ${MIN_SLUG_LENGTH} characters.`
            },
            maxLength: {
              value: MAX_SLUG_LENGTH,
              message: `At most ${MAX_SLUG_LENGTH} characters.`
            },
            pattern: {
              value: SLUG_PATTERN,
              message: 'Use lowercase letters, numbers and single hyphens between them.'
            }
          }}
          render={({ field }) => (
            <TextField
              {...field}
              label='URL slug'
              placeholder='expiry-calculator'
              slotProps={{ htmlInput: { maxLength: MAX_SLUG_LENGTH } }}
              error={Boolean(errors.slug)}
              helperText={
                errors.slug?.message ??
                'Becomes the published address, and is frozen once the app is first ' +
                  'approved. Availability is confirmed during review, not here.'
              }
              onChange={(event) => {
                setSlugManuallyEdited(true)
                field.onChange(event)
              }}
            />
          )}
        />

        <TextField
          label='Description'
          multiline
          rows={3}
          slotProps={{ htmlInput: { maxLength: 500 } }}
          error={Boolean(errors.description)}
          helperText={errors.description?.message}
          {...register('description', { required: 'Describe what the app does.' })}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Controller
            name='category'
            control={control}
            render={({ field }) => (
              <TextField {...field} select label='Category' fullWidth>
                {config.categories.map((category) => (
                  <MenuItem key={category} value={category}>
                    {humanizeOption(category)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name='intendedAudience'
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label='Intended audience'
                fullWidth
                helperText='Every app is reviewed to the customer-facing bar regardless.'
              >
                {config.audiences.map((audience) => (
                  <MenuItem key={audience} value={audience}>
                    {humanizeOption(audience)}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>

        <TextField
          label='Tags (optional)'
          placeholder='reporting, claims'
          error={Boolean(errors.tags)}
          helperText={
            errors.tags?.message ??
            `Comma-separated. Lowercase letters, numbers and hyphens, up to ${MAX_TAGS}.`
          }
          {...register('tags', {
            validate: (value) => {
              const tags = parseTags(value)
              if (tags.length > MAX_TAGS) return `At most ${MAX_TAGS} tags.`
              if (tags.some((tag) => !SLUG_PATTERN.test(tag))) {
                return 'Tags may use lowercase letters, numbers and hyphens only.'
              }
              // The broker caps each tag as well as the count; without this a long
              // tag round-trips to the 422 this validator exists to prevent.
              if (tags.some((tag) => tag.length > MAX_TAG_LENGTH)) {
                return `Each tag must be at most ${MAX_TAG_LENGTH} characters.`
              }
              return true
            }
          })}
        />

        <Button
          type='submit'
          variant='contained'
          size='large'
          disabled={isSubmitting}
          // getValues keeps the button label stable across re-renders without
          // subscribing the whole form to watch().
          aria-label={`Submit ${getValues('appName') || 'app'} for review`}
        >
          {isSubmitting ? 'Submitting…' : 'Submit for review'}
        </Button>
      </Stack>
    </Box>
  )
}

function FileExtension({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <Box component='code' sx={{ fontFamily: 'monospace', fontWeight: 600, color: 'text.primary' }}>
      {children}
    </Box>
  )
}
